<template>
    <team-user-details :userId="props.userId" :user="user" :loading="loading">
        <form-wrapper :sections="[{ id: 'details', label: 'Details' }]" hideNav>
            <vx-expandable-card id="details">
                <template v-slot:content>
                    <div class="flex flex-col gap-4">
                        <div>
                            <vx-label>Name</vx-label>
                            <div class="text-base" data-test="user-name">{{ user.name }}</div>
                        </div>
                        <div>
                            <vx-label>Email</vx-label>
                            <div class="text-base" data-test="user-email">{{ user.email }}</div>
                        </div>
                        <div v-if="FEATURES.contact_groups">
                            <vx-label>Target Access</vx-label>
                            <div class="flex items-center gap-1">
                                <vx-popover v-if="user.role !== 'none'" hover arrow placement="top">
                                    <meta-badge :icon="faCrown" color="amber" data-test="corp-badge"> </meta-badge>
                                    <template v-slot:content> Corporate Access </template>
                                </vx-popover>
                                <vx-popover hover arrow placement="top">
                                    <meta-badge :icon="faBuildingFlag" color="violet" data-test="ou-badge">
                                        {{ orgUnitCount }}
                                    </meta-badge>
                                    <template v-slot:content> Org Unit Access </template>
                                </vx-popover>
                                <vx-popover hover arrow placement="top">
                                    <meta-badge :icon="faGrid2Plus" color="slate" data-test="group-badge">
                                        {{ groupCount }}
                                    </meta-badge>
                                    <template v-slot:content> Group Access </template>
                                </vx-popover>
                            </div>
                        </div>
                        <div>
                            <vx-label>Team Role</vx-label>
                            <vx-selectable
                                v-if="canEditRole"
                                v-model="user.role"
                                size="lg"
                                :reduce="(option: { value: string }) => option.value"
                                :options="roleOptions"
                                :clearable="false"
                            />
                            <div v-else class="text-base" data-test="user-role">
                                {{ TEAM_USER_ROLES[user.role] || user.role }}
                            </div>
                        </div>
                    </div>
                </template>
            </vx-expandable-card>

            <vx-sticky-footer v-if="canEditRole">
                <vx-button :href="`/settings/teams/${teamId}#/membership`" class="w-20" size="lg" color="muted">
                    Cancel
                </vx-button>
                <vx-button
                    @click="submit()"
                    data-test="submit-button"
                    class="grow"
                    size="lg"
                    color="primary"
                    :disabled="!validation.$anyDirty"
                    :loading="processing"
                >
                    Update User
                </vx-button>
            </vx-sticky-footer>
        </form-wrapper>
    </team-user-details>
</template>

<script lang="ts" setup>
import { computed, inject, onMounted } from 'vue';
import {
    VxButton,
    VxExpandableCard,
    VxLabel,
    VxPopover,
    VxSelectable,
    VxStickyFooter,
} from '@voxie/frontend-components';
import { faBuildingFlag, faCrown, faGrid2Plus, faSquareArrowUpLeft } from '@fortawesome/pro-solid-svg-icons';
import TeamUserDetails from './TeamUserDetails.vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import MetaBadge from '~/components/general/MetaBadge.vue';
import { useTeamUserDetails } from './useTeamUserDetails';
import usersClient from '~/services/usersClient';
import { TEAM_USER_ROLES, teamRolesOptions } from '~/constants/roles';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { formatNumber } from '~/utils/helpers';
import type { Features } from '~/provides/features.types';

const teamId = Spark.state.currentTeam.id;
const currentUserId = Spark.state.user.id;
const isAdminOrOwner = Spark.isAdminOrOwner;
const FEATURES = inject('FEATURES') as Features;

const props = defineProps<{
    userId: number;
}>();

const roleOptions = teamRolesOptions({ exclude: ['owner'] });

const { user, loading, mount } = useTeamUserDetails();

onMounted(async () => {
    await mount(props.userId);
    validation.value.$reset();
});

const canEditRole = computed(() => {
    return isAdminOrOwner && user.value.role !== 'owner' && user.value.id !== currentUserId;
});

const orgUnitCount = computed(() =>
    ['admin', 'owner'].includes(user.value.role) ? 'All' : formatNumber(user.value.org_unit_count)
);

const groupCount = computed(() =>
    ['admin', 'owner'].includes(user.value.role) ? 'All' : formatNumber(user.value.group_count)
);

const save = async () => {
    await usersClient.updateUser(teamId, props.userId, {
        role: user.value.role,
    });
};

const { submit, processing, validation } = useFormSubmission({
    data: user,
    validation: {
        role: {
            $autoDirty: true,
        },
    },
    submit: save,
    preserveScroll: () => true,
    notification: () => ({
        message: 'User updated',
        action: {
            icon: faSquareArrowUpLeft,
            label: 'Back to Users',
            href: `/settings/teams/${teamId}#/membership`,
        },
    }),
});
</script>
